import {
  computed,
  getCurrentInstance,
  inject,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue'
import { isClient, useTimeoutFn } from '@vueuse/core'

import {
  defaultNamespace,
  useGlobalConfig,
  useId,
  useLocale,
  useLockscreen,
  useZIndex,
} from '@element-plus/hooks'
import {
  UPDATE_MODEL_EVENT,
  UPDATE_MODEL_VISIBLE,
} from '@element-plus/constants'
import { addUnit } from '@element-plus/utils'

import type { CSSProperties, Ref, SetupContext } from 'vue'
import type { DialogEmits, DialogProps } from './dialog'
const { t } = useLocale()
export const useDialog = (
  props: DialogProps,
  targetRef: Ref<HTMLElement | undefined>,
  type?: string
) => {
  const instance = getCurrentInstance()!
  const emit = instance.emit as SetupContext<DialogEmits>['emit']
  const { nextZIndex } = useZIndex()
  const injectBeforeClose = inject<VoidFunction | false>(
    'injectBeforeClose',
    false,
    false
  )
  let lastPosition = ''
  const titleId = useId()
  const bodyId = useId()
  const visible = ref(false)
  const closed = ref(false)
  const rendered = ref(false) // when desctroyOnClose is true, we initialize it as false vise versa
  const zIndex = ref(props.zIndex || nextZIndex())
  const cancelText = props.cancelText || t('el.popconfirm.cancelButtonText')
  const submitText = props.submitText || t('el.popconfirm.confirmButtonText')
  let openTimer: (() => void) | undefined = undefined
  let closeTimer: (() => void) | undefined = undefined

  const namespace = useGlobalConfig('namespace', defaultNamespace)

  const style = computed<CSSProperties>(() => {
    const style: CSSProperties = {}
    const varPrefix = `--${namespace.value}-dialog` as const
    if (!props.fullscreen) {
      if (props.top) {
        style[`${varPrefix}-margin-top`] = props.top
      }
      if (props.width) {
        style[`${varPrefix}-width`] = addUnit(props.width)
      }
    }
    if (props.zoomY) {
      style['minHeight'] = `${props.zoomMinHeight}px`
      style['maxHeight'] = `${props.zoomMaxHeight}px`
    }
    return style
  })

  const overlayDialogStyle = computed<CSSProperties>(() => {
    if (props.alignCenter) {
      return { display: 'flex' }
    }
    return {}
  })

  function afterEnter() {
    emit('opened')
  }

  function afterLeave() {
    emit('closed')
    emit(UPDATE_MODEL_EVENT, false)
    emit(UPDATE_MODEL_VISIBLE, false)
    if (props.destroyOnClose) {
      rendered.value = false
    }
  }

  function beforeLeave() {
    emit('close')
  }

  function open() {
    closeTimer?.()
    openTimer?.()

    if (props.openDelay && props.openDelay > 0) {
      ;({ stop: openTimer } = useTimeoutFn(() => doOpen(), props.openDelay))
    } else {
      doOpen()
    }
  }

  function close() {
    openTimer?.()
    closeTimer?.()

    if (props.closeDelay && props.closeDelay > 0) {
      ;({ stop: closeTimer } = useTimeoutFn(() => doClose(), props.closeDelay))
    } else {
      doClose()
    }
  }

  function handleClose() {
    function hide(shouldCancel?: boolean) {
      if (shouldCancel) return
      closed.value = true
      visible.value = false
    }

    if (props.beforeClose || injectBeforeClose) {
      if (props.beforeClose) {
        props.beforeClose(hide)
      } else {
        if (injectBeforeClose) {
          injectBeforeClose()
        }
      }
    } else {
      close()
    }
  }

  function handleSubmit() {
    if (props.submitHandle) {
      props.submitHandle(close)
    } else {
      close()
    }
  }

  function onModalClick() {
    // 是抽屉且有form表单，点击遮罩不能关闭弹框，否则关闭弹框
    const hasForm = targetRef.value?.querySelector(`.${namespace.value}-form`)
    if (props.closeOnClickModal || (type === 'drawer' && !hasForm)) {
      handleClose()
      emit('wrapperClose')
    }
  }

  function doOpen() {
    if (!isClient) return
    visible.value = true
  }

  function doClose() {
    visible.value = false
  }

  function onOpenAutoFocus() {
    emit('openAutoFocus')
  }

  function onCloseAutoFocus() {
    emit('closeAutoFocus')
  }

  function onFocusoutPrevented(event: CustomEvent) {
    if (event.detail?.focusReason === 'pointer') {
      event.preventDefault()
    }
  }

  if (props.lockScroll) {
    useLockscreen(visible)
  }

  function onCloseRequested() {
    if (props.closeOnPressEscape) {
      handleClose()
    }
  }

  watch([() => props.modelValue, () => props.visible], (val) => {
    if (val.some((item) => item)) {
      closed.value = false
      open()
      rendered.value = true // enables lazy rendering
      zIndex.value = props.zIndex ? zIndex.value++ : nextZIndex()
      // this.$el.addEventListener('scroll', this.updatePopper)
      nextTick(() => {
        emit('open')
        if (targetRef.value) {
          targetRef.value.scrollTop = 0
        }
      })
    } else {
      // this.$el.removeEventListener('scroll', this.updatePopper
      if (visible.value) {
        close()
      }
    }
  })

  watch(
    () => props.fullscreen,
    (val) => {
      if (!targetRef.value) return
      if (val) {
        lastPosition = targetRef.value.style.transform
        targetRef.value.style.transform = ''
      } else {
        targetRef.value.style.transform = lastPosition
      }
    }
  )

  onMounted(() => {
    if (props.modelValue || props.visible) {
      visible.value = true
      rendered.value = true // enables lazy rendering
      open()
    }
  })

  return {
    afterEnter,
    afterLeave,
    beforeLeave,
    handleClose,
    handleSubmit,
    onModalClick,
    close,
    doClose,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onCloseRequested,
    onFocusoutPrevented,
    titleId,
    bodyId,
    closed,
    style,
    overlayDialogStyle,
    rendered,
    visible,
    zIndex,
    cancelText,
    submitText,
  }
}
